/**
 * Compliance evidence (OPS 3.0b) — Documents-module lens on the unified store
 * rows (client_documents, area=COMPLIANCE + complianceItemId): jurisdiction
 * folders (derived from the client's jurisdiction) → one folder per compliance
 * item (service + period) → evidence files with upload/preview/download/delete.
 *
 * Uploads/deletes need compliance.edit (TeamLead+, same bar as item editing);
 * reads follow staff scoping server-side. Evidence is indicator-only — it
 * never gates item completion.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JURISDICTION_LABELS,
  ITEM_STATUS_LABELS,
  type ClientDocumentOutput,
  type ComplianceFolder,
  type ItemStatus,
} from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { fmtDate } from '../lib/date.js';
import { FilePreviewModal } from './FilePreviewModal.js';
import { DOCS_ACCEPT, OFFICE_MIME } from '../pages/DocumentsPage.js';

const MAX_FILE_BYTES = 20 * 1024 * 1024;

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${getAccessToken() ?? ''}` };
}

function jurisdictionLabel(j: string | null): string {
  if (!j) return 'Unspecified jurisdiction';
  return JURISDICTION_LABELS[j as keyof typeof JURISDICTION_LABELS] ?? j;
}

/** Documents-module lens: jurisdiction → item folders → evidence files. */
export function ComplianceEvidenceFolders({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);
  const [uploadItem, setUploadItem] = useState<{ id: string; label: string } | null>(null);

  const folders = useQuery({
    queryKey: ['compliance-folders', clientId],
    queryFn: async () => {
      const res = await fetch(`/v1/documents/client/${clientId}/compliance-folders`, { headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { folders: ComplianceFolder[] } };
      return body.data.folders;
    },
  });

  // All evidence rows for the client in one fetch; grouped client-side per item.
  const docs = useQuery({
    queryKey: ['area-documents', clientId, 'COMPLIANCE'],
    queryFn: async () => {
      const res = await fetch(`/v1/documents/client/${clientId}?area=COMPLIANCE`, { headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: ClientDocumentOutput[] } };
      return body.data.items;
    },
  });

  function toggleItem(id: string) {
    setOpenItems((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function openPreview(d: ClientDocumentOutput) {
    const res = await fetch(`/v1/legal-documents/${d.id}/file`, { headers: authHeaders() });
    if (!res.ok) return;
    const url = URL.createObjectURL(await res.blob());
    if (OFFICE_MIME.test(d.mime)) {
      // Office files are download-only (the API serves them as attachments).
      const a = document.createElement('a');
      a.href = url;
      a.download = d.originalName;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old.url);
      return { url, name: d.originalName };
    });
  }

  function closePreview() {
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old.url);
      return null;
    });
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['compliance-folders', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['area-documents', clientId, 'COMPLIANCE'] });
    void queryClient.invalidateQueries({ queryKey: ['documents-tree'] });
    void queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
  }

  if (folders.isLoading) return <p className="text-muted text-sm p-3">Loading evidence folders…</p>;
  if (folders.error) return <p className="text-rose-400 text-sm p-3">{(folders.error as Error).message}</p>;

  const byItem = new Map<string, ClientDocumentOutput[]>();
  for (const d of docs.data ?? []) {
    if (!d.complianceItemId) continue;
    const list = byItem.get(d.complianceItemId) ?? [];
    list.push(d);
    byItem.set(d.complianceItemId, list);
  }

  const list = folders.data ?? [];
  return (
    <div className="space-y-3">
      {list.length === 0 && (
        <p className="text-muted text-sm p-3">No compliance jurisdiction on file for this client yet.</p>
      )}
      {list.map((folder) => (
        <div key={folder.jurisdiction ?? 'none'} className="border border-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-bg-base border-b border-border flex items-center gap-2">
            <span className="text-sm text-white">📁 {jurisdictionLabel(folder.jurisdiction)}</span>
            <span className="text-xs text-muted">{folder.items.length} item{folder.items.length === 1 ? '' : 's'}</span>
          </div>
          {folder.items.length === 0 && (
            <p className="text-xs text-muted px-3 py-2">No compliance items in this jurisdiction.</p>
          )}
          {folder.items.map((item) => {
            const label = `${item.serviceType}${item.periodValue ? ` ${item.periodValue}` : ''}`;
            const evidence = byItem.get(item.id) ?? [];
            const open = openItems.has(item.id);
            return (
              <div key={item.id} className="border-b border-border/50 last:border-0">
                <div className="flex items-center gap-2 px-3 py-2">
                  <button onClick={() => toggleItem(item.id)} className="flex-1 flex items-center gap-2 text-left min-w-0">
                    <span className="text-muted text-xs">{open ? '▾' : '▸'}</span>
                    <span className="text-sm text-white truncate">📂 {label}</span>
                    <span className="text-xs text-muted shrink-0">{ITEM_STATUS_LABELS[item.status as ItemStatus] ?? item.status}</span>
                    {item.evidenceCount > 0 && (
                      <span className="text-xs text-muted shrink-0">📎 {item.evidenceCount}</span>
                    )}
                  </button>
                  {canDo('compliance.edit') && (
                    <button
                      onClick={() => setUploadItem({ id: item.id, label })}
                      className="text-xs text-accent hover:text-white border border-border hover:border-accent px-2 py-1 rounded shrink-0"
                    >
                      Upload
                    </button>
                  )}
                </div>
                {open && (
                  <div className="px-3 pb-2">
                    <EvidenceRows
                      docs={evidence}
                      onPreview={(d) => void openPreview(d)}
                      onChanged={invalidate}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
      {preview && <FilePreviewModal url={preview.url} name={preview.name} onClose={closePreview} />}
      {uploadItem && (
        <EvidenceUploadModal
          itemId={uploadItem.id}
          itemLabel={uploadItem.label}
          onClose={() => setUploadItem(null)}
          onDone={() => { setUploadItem(null); invalidate(); }}
        />
      )}
    </div>
  );
}

function EvidenceRows({ docs, onPreview, onChanged, empty = 'No evidence files in this folder yet.' }: {
  docs: ClientDocumentOutput[];
  onPreview: (d: ClientDocumentOutput) => void;
  onChanged: () => void;
  empty?: string;
}) {
  const { canDo } = useCapabilities();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleDelete(d: ClientDocumentOutput) {
    if (!window.confirm(`Remove "${d.originalName}" from evidence? The file is deleted from the store.`)) return;
    setError(null);
    setDeletingId(d.id);
    try {
      const res = await fetch(`/v1/documents/evidence/${d.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw await httpError(res);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  }

  if (docs.length === 0) return <p className="text-xs text-muted py-2">{empty}</p>;
  return (
    <div>
      {error && <p className="text-xs text-rose-400 py-1">{error}</p>}
      <ul className="divide-y divide-border/50">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-3 py-1.5">
            <button onClick={() => onPreview(d)} className="text-sm text-accent hover:underline text-left truncate">
              {d.originalName}
            </button>
            <span className="flex items-center gap-3 shrink-0">
              <span className="text-xs text-muted">
                {d.uploadedByName ? `${d.uploadedByName} · ` : ''}{fmtDate(d.createdAt)}
              </span>
              {canDo('compliance.edit') && (
                <button
                  onClick={() => void handleDelete(d)}
                  disabled={deletingId === d.id}
                  className="text-xs btn-ghost-danger border px-2 py-0.5 rounded disabled:opacity-50"
                >
                  {deletingId === d.id ? '…' : 'Delete'}
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EvidenceUploadModal({ itemId, itemLabel, onClose, onDone }: {
  itemId: string;
  itemLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError('Choose a file first.'); return; }
    if (file.size > MAX_FILE_BYTES) { setError('File too large — max 20 MB.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/v1/documents/item/${itemId}/upload`, { method: 'POST', headers: authHeaders(), body: fd });
      const body = await res.json() as { ok?: boolean; error?: { message?: string } };
      if (!res.ok || !body.ok) {
        setError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
        return;
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={handleUpload} className="bg-bg-panel border border-border rounded-xl w-full max-w-md p-5 space-y-3 shadow-2xl animate-pop">
        <h3 className="text-sm font-semibold text-white">File evidence on {itemLabel}</h3>
        <p className="text-xs text-muted">
          Filed returns, FTA / Companies House receipts, confirmations. PDF, JPG, PNG or WebP, max 20 MB.
        </p>
        <input
          type="file"
          accept={DOCS_ACCEPT}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-muted file:mr-3 file:bg-bg-base file:border file:border-border file:rounded-lg file:px-3 file:py-1.5 file:text-sm file:text-white hover:file:border-accent"
        />
        {error && <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            type="submit"
            disabled={submitting || !file}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </form>
    </div>
  );
}
